import { HuddleClient, HuddleProvider } from '@huddle01/react';
import { useState, useMemo, useCallback } from 'react';
import { VoiceRoom } from './components/VoiceRoom';
import type { RoomConfig } from './types';
import './App.css';

// Huddle01 Project ID from config
const PROJECT_ID = 'pi_uajKetB3sZfApMku';

// Default WebSocket URL - can be overridden with ?wsUrl= param
// Use WSS via Cloudflare proxy (handles SSL termination)
const DEFAULT_WS_URL = 'wss://ws.hytaleproleague.com';

type AuthState = 'input' | 'connecting' | 'authenticated' | 'error';



/**
 * Get WebSocket URL from URL params or default
 */
function getWsUrl(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('wsUrl') || DEFAULT_WS_URL;
}

/**
 * Short code input form
 */
function CodeInput({ 
  onSubmit, 
  state, 
  error,
  testMode,
  onTestModeChange,
}: { 
  onSubmit: (code: string) => void;
  state: AuthState;
  error: string | null;
  testMode: boolean;
  onTestModeChange: (enabled: boolean) => void;
}) {
  const [code, setCode] = useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim().length >= 4) {
      onSubmit(code.trim().toUpperCase());
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow alphanumeric, max 6 chars
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    setCode(value);
  };
  
  return (
    <div className="code-input-page">
      <div className="code-input-container">
        <h1>ProxChat</h1>
        <p className="subtitle">Proximity Voice Chat for Hytale</p>
        
        <form onSubmit={handleSubmit} className="code-form">
          <label htmlFor="code-input">Enter your connection code</label>
          <p className="hint">Run <code>/vc join</code> in-game to get a code</p>
          
          <input
            id="code-input"
            type="text"
            value={code}
            onChange={handleChange}
            placeholder="XXXX"
            className="code-input-field"
            autoFocus
            autoComplete="off"
            disabled={state === 'connecting'}
          />
          
          <button 
            type="submit" 
            className="connect-button"
            disabled={code.length < 4 || state === 'connecting'}
          >
            {state === 'connecting' ? 'Connecting...' : 'Connect'}
          </button>
          
          {state === 'error' && error && (
            <div className="auth-error">{error}</div>
          )}
        </form>
        
        <div className="instructions">
          <h3>How to connect:</h3>
          <ol>
            <li>Join a Hytale server with ProxChat enabled</li>
            <li>Run <code>/vc join</code> to get your connection code</li>
            <li>Enter the code above and click Connect</li>
            <li>Allow microphone access when prompted</li>
          </ol>
        </div>
      </div>
      
      {/* Dev/Test Mode toggle at bottom */}
      <div className="test-mode-toggle">
        <label className="test-mode-label">
          <input
            type="checkbox"
            checked={testMode}
            onChange={(e) => onTestModeChange(e.target.checked)}
          />
          <span>Dev/Test Mode</span>
        </label>
        {testMode && (
          <p className="test-mode-hint">
            Joins the room as a dev/test user (server uses fixed coordinates)
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Main App component
 */
function App() {
  const [authState, setAuthState] = useState<AuthState>('input');
  const [authError, setAuthError] = useState<string | null>(null);
  const [config, setConfig] = useState<RoomConfig | null>(null);
  const [testMode, setTestMode] = useState(false);
  
  const wsUrl = useMemo(() => getWsUrl(), []);

  // Create Huddle client
  const huddleClient = useMemo(() => {
    return new HuddleClient({
      projectId: PROJECT_ID,
    });
  }, []);

  const handleCodeSubmit = useCallback((code: string) => {
    setAuthState('connecting');
    setAuthError(null);
    
    // Connect to WebSocket and authenticate
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('[ProxChat] Connected to server, sending auth code...');
      ws.send(JSON.stringify({
        type: 'auth',
        code: code,
        testMode: testMode,
      }));
    };
    
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'auth_success') {
          console.log('[ProxChat] Authentication successful, testMode:', testMode);
          setConfig({
            roomId: message.roomId,
            token: message.token,
            peerId: message.peerId,
            wsUrl: wsUrl,
            testMode: testMode,
          });
          setAuthState('authenticated');
          // Close this connection - VoiceRoom will create its own
          ws.close();
        } else if (message.type === 'auth_error') {
          console.error('[ProxChat] Authentication failed:', message.error);
          setAuthError(message.error);
          setAuthState('error');
          ws.close();
        }
      } catch (e) {
        console.error('[ProxChat] Failed to parse message:', e);
      }
    };
    
    ws.onerror = () => {
      setAuthError('Failed to connect to game server. Is the server running?');
      setAuthState('error');
    };
    
    ws.onclose = () => {
      if (authState === 'connecting') {
        setAuthError('Connection closed unexpectedly');
        setAuthState('error');
      }
    };
    
    // Timeout after 10 seconds
    setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
        if (authState === 'connecting') {
          setAuthError('Connection timed out');
          setAuthState('error');
          ws.close();
        }
      }
    }, 10000);
  }, [wsUrl, authState, testMode]);

  // Show code input if not authenticated
  if (!config) {
    return (
      <CodeInput 
        onSubmit={handleCodeSubmit}
        state={authState}
        error={authError}
        testMode={testMode}
        onTestModeChange={setTestMode}
      />
    );
  }

  return (
    <HuddleProvider client={huddleClient}>
      <VoiceRoom config={config} />
    </HuddleProvider>
  );
}

export default App;
